import React, { useState } from 'react';
import Sidebar from './Sidebar';
import OpsDispatchBoard from '../pages/OpsDispatchBoard';
import OpsCalendar from '../pages/OpsCalendar';
import OpsDrivers from '../pages/OpsDrivers';
import OpsDispatchers from '../pages/OpsDispatchers';
import OpsSettings from '../pages/OpsSettings';
import OpsEmailLog from '../pages/OpsEmailLog';
import { ToastProvider } from '../ui/primitives';
import { IconRoute, IconCalendar, IconUsers, IconBuilding, IconSend } from '../ui/icons';
import './OpsApp.css';

// Two ops profiles share this shell:
//   • dispatcher (isAdmin=false): Run → Dispatch + Calendar. No staff management.
//   • admin (isAdmin=true): the above PLUS Manage → Drivers + Dispatchers (each provisions
//     real Cognito logins via /api/admin/*).
// Other repo pages (OpsDashboard, OpsTrips, OpsFleet, OpsBilling, OpsSettings, …) remain
// unwired from nav/routing — re-add to navItems and renderPage() to restore.
function buildNavItems(isAdmin) {
  const items = [
    { section: 'Run', items: [
      { id: 'dispatch', label: 'Dispatch', icon: <IconRoute size={18} /> },
      { id: 'calendar', label: 'Calendar', icon: <IconCalendar size={18} /> },
    ]},
  ];
  if (isAdmin) {
    items.push({ section: 'Manage', items: [
      { id: 'op-drivers', label: 'Drivers', icon: <IconUsers size={18} /> },
      { id: 'op-dispatchers', label: 'Dispatchers', icon: <IconUsers size={18} /> },
      { id: 'op-emails', label: 'Email log', icon: <IconSend size={18} /> },
      { id: 'op-settings', label: 'Configurability', icon: <IconBuilding size={18} /> },
    ]});
  }
  return items;
}

function OpsApp({ isAdmin = false, authSession, userName, onLogout }) {
  const [activePage, setActivePage] = useState('dispatch');
  const NAV_ITEMS = buildNavItems(isAdmin);

  const renderPage = () => {
    if (activePage === 'dispatch') return <OpsDispatchBoard authSession={authSession} />;
    if (activePage === 'calendar') return <OpsCalendar authSession={authSession} />;
    // Manage pages are admin-only; guard render in case state is stale after a role change.
    if (activePage === 'op-drivers') return isAdmin ? <OpsDrivers authSession={authSession} /> : null;
    if (activePage === 'op-dispatchers') return isAdmin ? <OpsDispatchers authSession={authSession} /> : null;
    if (activePage === 'op-emails') return isAdmin ? <OpsEmailLog authSession={authSession} /> : null;
    if (activePage === 'op-settings') return isAdmin ? <OpsSettings authSession={authSession} /> : null;
    return null;
  };

  // Pages that render their own in-page header (and so opt out of the shared
  // layout header) — keeps these from showing two titles.
  const SELF_HEADERED = ['dispatch', 'calendar', 'op-drivers', 'op-dispatchers'];
  const showHeader = !SELF_HEADERED.includes(activePage);

  return (
    <ToastProvider>
      <div className="h-screen overflow-hidden flex bg-slate-50">
        <Sidebar
          items={NAV_ITEMS}
          activeItem={activePage}
          onItemChange={setActivePage}
          userName={userName}
          roleLabel={isAdmin ? 'Admin' : 'Dispatch'}
          onLogout={onLogout}
        />
        <main className="ops-main">
          {showHeader && (
            <div className="ops-page-header">
              <h2 className="ops-page-title">
                {(() => { const item = NAV_ITEMS.flatMap(g => g.items || [g]).find(i => i.id === activePage); return item ? item.label : ''; })()}
              </h2>
            </div>
          )}
          <div className="ops-content">
            {renderPage()}
          </div>
        </main>
      </div>
    </ToastProvider>
  );
}

export default OpsApp;
